import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";

interface Rule {
  id: string;
  name: string;
  description: string | null;
  ruleType: string;
  outcome: "block" | "warn";
  scopeType: string;
  isEnabled: boolean;
  activeReviewAt: string | null;
  updatedAt: string;
}

export default function RulesList() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: rules, isLoading } = useQuery<Rule[]>({ queryKey: ["/api/rules"] });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      apiRequest("PATCH", `/api/rules/${id}`, { isEnabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/rules"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/rules/${id}`),
    onSuccess: () => {
      toast({ title: t("rules.list.deleted", "Rule deleted") });
      queryClient.invalidateQueries({ queryKey: ["/api/rules"] });
    },
  });

  if (isLoading) return <div className="p-6">{t("common.loading")}</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="page-rules-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("rules.list.title", "Order Rules")}</h1>
          <p className="text-sm text-muted-foreground">{t("rules.list.subtitle", "Configurable validation rules that block or warn on orders.")}</p>
        </div>
        <div className="flex gap-2">
          <Link href="/admin/rules/audit"><Button variant="outline" data-testid="button-view-audit">{t("rules.list.viewAudit", "Audit log")}</Button></Link>
          <Link href="/admin/rules/new"><Button data-testid="button-new-rule">{t("rules.list.new", "+ New rule")}</Button></Link>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("rules.list.type", "Type")}</TableHead>
              <TableHead>{t("rules.list.outcome", "Outcome")}</TableHead>
              <TableHead>{t("rules.list.scope", "Scope")}</TableHead>
              <TableHead>{t("rules.list.review", "Review")}</TableHead>
              <TableHead className="text-end">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rules || []).length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t("rules.list.empty", "No rules yet. Click + New rule to create one.")}</TableCell></TableRow>
            ) : (
              (rules || []).map((r) => (
                <TableRow key={r.id} data-testid={`row-rule-${r.id}`}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                    {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                  </TableCell>
                  <TableCell><code className="text-xs">{r.ruleType}</code></TableCell>
                  <TableCell>
                    <Badge variant={r.outcome === "block" ? "destructive" : "secondary"}>{r.outcome}</Badge>
                  </TableCell>
                  <TableCell>{r.scopeType}</TableCell>
                  <TableCell className="text-xs">
                    {r.activeReviewAt ? new Date(r.activeReviewAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-end">
                    <div className="flex items-center justify-end gap-2">
                      <Switch
                        checked={r.isEnabled}
                        onCheckedChange={(v) => toggleMutation.mutate({ id: r.id, isEnabled: v })}
                        data-testid={`switch-rule-${r.id}`}
                      />
                      <Link href={`/admin/rules/${r.id}`}><Button size="sm" variant="outline" data-testid={`button-edit-${r.id}`}>{t("common.edit")}</Button></Link>
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm(t("rules.list.deleteConfirm", "Delete this rule?"))) deleteMutation.mutate(r.id); }} data-testid={`button-delete-${r.id}`}>{t("common.delete")}</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
